import { all, call, fork, takeEvery } from '@redux-saga/core/effects';
import { createReducer, createSelector } from '@reduxjs/toolkit';
import { KLEHIERARCHY_INCLUDE } from '../../constants/api';
import {
  getAllHearingTypesSuccess,
  getAllKleHierarchiesInitialize,
  getAllKleHierarchiesSuccess,
  getAllKleHierarchiesError,
} from '../actions/api';
import { KleHierarchyConnector } from '../connectors/kleHierarchyConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { updateDtoList } from '../helpers/reducerHelpers';
import { generateGetAllApiCall } from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { allKleMappingsSelector } from './kleMappingSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /kleHierarchy
//
// KleHierarchy is included in the following endpoints
//  - GET /hearingType

// -------- Reducer --------
const kleHierarchyReducer = createReducer(
  {
    all: [],
    isFetchingKleHierarchies: false,
  },
  (builder) => {
    builder
      .addCase(getAllHearingTypesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const KleHierarchies = data.filter(
          (include) => include.type === KLEHIERARCHY_INCLUDE,
        );
        return updateDtoList(state, 'all', KleHierarchies);
      })
      .addCase(getAllKleHierarchiesInitialize, (state) => {
        state.isFetchingKleHierarchies = true;
      })
      .addCase(getAllKleHierarchiesError, (state) => {
        state.isFetchingKleHierarchies = false;
      })
      .addCase(getAllKleHierarchiesSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingKleHierarchies: false,
        });
        const kleHierarchies = getRequestData(action);
        return updateDtoList(newState, 'all', kleHierarchies);
      });
  },
);

// -------- Sagas ----------
export function* kleHierarchySaga() {
  yield all([fork(getAllKleHierarchiesSaga)]);
}

function* getAllKleHierarchiesSaga() {
  yield takeEvery(
    getAllKleHierarchiesInitialize.type,
    generateGetAllApiCall(KleHierarchyConnector, function* (connector) {
      return yield call([connector, connector.getKleHierarchies]);
    }),
  );
}

// -------- Selectors --------
const selectGetAllKleHierarchies = (state) =>
  selectAllFromApiState(state, 'kleHierarchy');

const allKleHierarchiesSelector = createSelector(
  selectGetAllKleHierarchies,
  (KleHierarchies) => KleHierarchies,
);
const allKleHierarchiesByHearingTypeIdSelector = createSelector(
  [allKleMappingsSelector, allKleHierarchiesSelector],
  (allKleMappings, allKleHierarchies) => {
    if (!(allKleMappings && allKleMappings.length)) {
      return {};
    }

    const allKleHierarchiesDict = allKleHierarchies.reduce(
      (acc, kleHierarchy) => {
        acc[getEntityId(kleHierarchy)] = kleHierarchy;

        return acc;
      },
      {},
    );

    return allKleMappings.reduce((acc, kleMapping) => {
      const hearingTypeId = getRelId(kleMapping, 'hearingType');
      const kleHierarchyId = getRelId(kleMapping, 'kleHierarchy');

      if (kleHierarchyId in allKleHierarchiesDict) {
        acc[hearingTypeId] = acc[hearingTypeId] || [];
        acc[hearingTypeId].push(allKleHierarchiesDict[kleHierarchyId]);
      }

      return acc;
    }, {});
  },
);

const kleHierarchyState = (state) => state.api.kleHierarchy;

const isFetchingKleHierarchiesSelector = createSelector(
  kleHierarchyState,
  (state) => state.isFetchingKleHierarchy,
);

// -------- Exporting --------
export {
  allKleHierarchiesSelector,
  allKleHierarchiesByHearingTypeIdSelector,
  isFetchingKleHierarchiesSelector,
};
export default kleHierarchyReducer;
